"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  FormControl,
  FormLabel,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  HStack,
  Box,
} from "@chakra-ui/react";

interface ResponsivenessSliderProps {
  value: number | undefined;
  onChange: (value: number) => Promise<boolean> | void;
}

export const ResponsivenessSlider: React.FC<ResponsivenessSliderProps> = ({
  value = 1,
  onChange,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isUpdating, setIsUpdating] = useState(false);

  // keep local state in sync if parent updates `value`
  useEffect(() => setLocalValue(value), [value]);

  const handleChange = (newValue: number) => {
    setLocalValue(newValue);
  };

  const handleChangeEnd = async (newValue: number) => {
    if (newValue === value) return;
    setIsUpdating(true);
    try {
      await onChange(newValue);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <FormControl>
      <FormLabel fontSize="sm" fontWeight="medium" color="gray.700" mb={1}>
        Responsiveness
      </FormLabel>
      <Text fontSize="xs" color="gray.600" mb={2}>
        Control how fast the agent responds after users finish speaking.
      </Text>

      <HStack spacing={4}>
        <Slider
          aria-label="Responsiveness"
          min={0}
          max={1}
          step={0.01}
          value={localValue}
          onChange={handleChange}
          onChangeEnd={handleChangeEnd}
          isDisabled={isUpdating}
          flex={1}
        >
          <SliderTrack bg="gray.200">
            <SliderFilledTrack bg="primary.500" />
          </SliderTrack>
          <SliderThumb boxSize={4} />
        </Slider>

        <Box minW="28px" textAlign="center">
          <Text fontSize="sm" fontWeight="medium" color="gray.700">
            {localValue.toFixed(2)}
          </Text>
        </Box>
      </HStack>
    </FormControl>
  );
};
